import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import {
  Send,
  Download,
  Delete,
  Add,
  Refresh,
  Assignment,
  Inventory2,
  AutoAwesome,
  CloudUpload
} from '@mui/icons-material';
import { useStorefront3DStore } from '../store/useStorefront3DStore';
import { useInventory } from '../../../../../contexts/InventoryContext';
import axios from 'axios';

const GenerationPanel = () => {
  const { addGeneratedModel, updateItem } = useStorefront3DStore();
  const { inventory } = useInventory();
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('low quality, low resolution, low poly, ugly');
  const [artStyle, setArtStyle] = useState('realistic');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStatus, setGenerationStatus] = useState('');
  const [generatedModels, setGeneratedModels] = useState([]);
  const [error, setError] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedModelForAssignment, setSelectedModelForAssignment] = useState(null);

  const artStyles = [
    'realistic',
    'cartoon',
    'low-poly',
    'sculpture',
    'pbr'
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt for 3D model generation');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGenerationProgress(0);
    setGenerationStatus('Starting generation...');

    try {
      // Start preview generation
      const response = await axios.post('/api/meshy/generate', {
        prompt: prompt.trim(),
        negativePrompt: negativePrompt.trim(),
        artStyle,
        mode: 'preview'
      });

      const taskId = response.data.taskId;
      setGenerationStatus('Generating preview model...');
      
      // Poll for completion
      await pollTaskStatus(taskId, 'preview');

    } catch (error) {
      console.error('Generation failed:', error);
      setError(error.response?.data?.message || 'Failed to generate 3D model');
      setIsGenerating(false);
    }
  };

  const pollTaskStatus = async (taskId, stage) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/api/meshy/status/${taskId}`);
        const { status, progress, modelUrls } = response.data;

        setGenerationProgress(progress || 0);
        setGenerationStatus(`${stage === 'preview' ? 'Preview' : 'Refining'} - ${status}`);

        if (status === 'SUCCEEDED') {
          clearInterval(pollInterval);
          
          if (stage === 'preview') {
            // Start refine stage
            setGenerationStatus('Starting refinement...');
            const refineResponse = await axios.post('/api/meshy/generate', {
              mode: 'refine',
              previewTaskId: taskId
            });
            await pollTaskStatus(refineResponse.data.taskId, 'refine');
          } else {
            // Final model ready
            const newModel = {
              id: Date.now(),
              prompt,
              artStyle,
              taskId,
              modelUrls,
              createdAt: new Date().toISOString()
            };
            
            setGeneratedModels(prev => [newModel, ...prev]);
            setIsGenerating(false);
            setGenerationStatus('Model generated successfully!');
            setPrompt('');
          }
        } else if (status === 'FAILED') {
          clearInterval(pollInterval);
          setError('Model generation failed');
          setIsGenerating(false);
        }
      } catch (error) {
        clearInterval(pollInterval);
        setError('Failed to check generation status');
        setIsGenerating(false);
      }
    }, 5000);
  };

  const handleAddToScene = async (model) => {
    try {
      // Download and add model to 3D scene
      const response = await axios.post('/api/meshy/add-to-scene', {
        modelUrl: model.modelUrls.glb,
        prompt: model.prompt
      });

      addGeneratedModel({
        id: model.id,
        name: model.prompt,
        url: response.data.localUrl,
        position: [0, 0, 0],
        rotation: [0, 0, 0],
        scale: [1, 1, 1]
      });

      setGenerationStatus('Model added to scene!');
    } catch (error) {
      setError('Failed to add model to scene');
    }
  };

  const handleDownload = (model) => {
    window.open(model.modelUrls.glb, '_blank');
  };

  const handleDeleteModel = (modelId) => {
    setGeneratedModels(prev => prev.filter(m => m.id !== modelId));
  };

  const handleAssignToProduct = (model) => {
    setSelectedModelForAssignment(model);
    setAssignDialogOpen(true);
  };

  const handleConfirmAssignment = async (productId) => {
    if (!selectedModelForAssignment) return;

    try {
      // Update the inventory item with the 3D model path
      const response = await axios.put(`/api/inventory/${productId}`, {
        model3d: selectedModelForAssignment.url
      });

      // Update the item in the scene if it's already there
      updateItem(productId, { model3d: selectedModelForAssignment.url });

      setGenerationStatus(`Model assigned to product successfully!`);
      setAssignDialogOpen(false);
      setSelectedModelForAssignment(null);
    } catch (error) {
      setError('Failed to assign model to product');
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>
        3D Model Generation
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generate custom 3D models from text descriptions using AI
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Generation Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            label="Describe your 3D model"
            placeholder="e.g., a wooden chair, modern lamp, vintage sofa..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={isGenerating}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Negative Prompt (optional)"
            placeholder="What to avoid in the model"
            value={negativePrompt}
            onChange={(e) => setNegativePrompt(e.target.value)}
            disabled={isGenerating}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Art Style</InputLabel>
            <Select
              value={artStyle}
              onChange={(e) => setArtStyle(e.target.value)}
              disabled={isGenerating}
            >
              {artStyles.map(style => (
                <MenuItem key={style} value={style}>
                  {style.charAt(0).toUpperCase() + style.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isGenerating && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {generationStatus}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={generationProgress} 
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {generationProgress}% complete
              </Typography>
            </Box>
          )}
        </CardContent>

        <CardActions>
          <Button
            variant="contained"
            startIcon={<AutoAwesome />}
            onClick={handleGenerate}
            disabled={isGenerating || !prompt.trim()}
            fullWidth
          >
            {isGenerating ? 'Generating...' : 'Generate 3D Model'}
          </Button>
        </CardActions>
      </Card>

      <Divider sx={{ mb: 2 }} />

      {/* Generated Models */}
      <Typography variant="h6" gutterBottom>
        Generated Models ({generatedModels.length})
      </Typography>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Grid container spacing={2}>
          {generatedModels.map((model) => (
            <Grid item xs={12} key={model.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Typography variant="subtitle1" noWrap sx={{ flex: 1, mr: 1 }}>
                      {model.prompt}
                    </Typography>
                    <Chip 
                      label={model.artStyle} 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary">
                    Generated: {new Date(model.createdAt).toLocaleString()}
                  </Typography>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Box>
                    <Tooltip title="Add to Scene">
                      <IconButton 
                        size="small" 
                        onClick={() => handleAddToScene(model)}
                        color="primary"
                      >
                        <Add />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Assign to Product">
                      <IconButton 
                        size="small" 
                        onClick={() => handleAssignToProduct(model)}
                        color="secondary"
                      >
                        <Assignment />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Download GLB">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDownload(model)}
                      >
                        <Download />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Tooltip title="Delete">
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteModel(model.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {generatedModels.length === 0 && (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 4,
              color: 'text.secondary'
            }}
          >
            <CloudUpload sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1">
              No models generated yet
            </Typography>
            <Typography variant="body2">
              Create your first 3D model using the form above
            </Typography>
          </Box>
        )}
      </Box>

      {/* Product Assignment Dialog */}
      <Dialog 
        open={assignDialogOpen} 
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Model to Product
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select an inventory product to assign this 3D model to:
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Model: {selectedModelForAssignment?.prompt}
          </Typography>
          
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {inventory?.map((product) => (
              <ListItem 
                key={product.id}
                button
                onClick={() => handleConfirmAssignment(product.id)}
                sx={{ 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 1, 
                  mb: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={product.images?.[0]?.url}
                    variant="rounded"
                  >
                    <Inventory2 />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={product.name}
                  secondary={`SKU: ${product.sku} • Stock: ${product.quantity}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GenerationPanel;
